import type { ReactElement } from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import WebIcon from '@mui/icons-material/Web';
import type { ModuleDefinition } from '../../../app/moduleRegistry';
import { siteModule } from './site';
import { headerModule } from './header';
import { siteTitleModule } from './siteTitle';
import { sliderModule } from './slider';
import { contactModule } from './contact';
import { aboutModule } from './about';
import { contractModule } from './contract';

/**
 * CMS SITE admin screens (Site / Header / SiteTitle / Slider / Contact / About / Contract) —
 * the "B2C site management" resources gated behind the `HasCmsB2C` feature flag
 * (Master-GSA-only). One combined module aggregating `siteModule` (the only one of the 7
 * sub-modules that still contributes a route/menu item — see ./site/index.tsx) with its 6
 * sibling sub-modules, which now contribute empty `routes`/`menuItems` arrays: their list
 * pages (Header/SiteTitle/Slider/Contact/About/Contract) are rendered as tabs inside
 * `SiteDetailPage` instead of standalone routed pages, matching the aggregator pattern used
 * elsewhere in this codebase (see modules/system/index.tsx and modules/corporate/index.tsx).
 *
 * This module owns ONLY the "Site" resource family. A sibling `modules/cms/content/**`
 * (owned separately) covers the rest of the CMS surface; a hand-written
 * `modules/cms/index.tsx` combines `cmsSiteModule` with that sibling's module — this file
 * does not import or reference it.
 *
 * Route/shape confirmation notes (local-backend swagger + live curl against a real
 * Master-GSA session, 2026-08-10) — see api/types/cmsSite.ts for full detail:
 * - All 7 resources' Add/Edit/Delete/Get routes are confirmed to exist in swagger.
 * - Site additionally has `GetSiteById`; the other 6 do NOT have a by-id route.
 * - Response field names for all 7 `Get*` routes were confirmed via live curl.
 * - Live row data was only practically obtainable for About (embedded in a small enough
 *   slice of a `Site` payload) — the other 6 resources' own scalar fields rely on swagger
 *   (treated as reliable for field names/types per this codebase's convention), because the
 *   backend recursively re-serializes each row's full parent `Site` graph, making a full
 *   live dump impractical (~740MB for a single `GetSites` call against this test DB).
 */
const withIcons: Record<string, ReactElement> = {
  'cms/site': <LanguageIcon />,
};

const subModules: ModuleDefinition[] = [
  siteModule,
  headerModule,
  siteTitleModule,
  sliderModule,
  contactModule,
  aboutModule,
  contractModule,
];

export const cmsSiteModule: ModuleDefinition = {
  routes: subModules.flatMap((m) => m.routes),
  menuItems: subModules.flatMap((m, moduleIdx) =>
    m.menuItems.map((item, itemIdx) => ({
      ...item,
      label: item.label.replace(/^CMS Site:\s*/, ''),
      icon: item.icon ?? withIcons[item.path],
      group: 'B2C Yönetimi',
      groupIcon: moduleIdx === 0 && itemIdx === 0 ? <WebIcon /> : undefined,
    }))
  ),
};
